import { useState } from "react";

import { ColaboradorFormModal } from "@/components/ColaboradorFormModal";
import { useAtualizarColaborador, useColaboradores, useEmpresas } from "@/hooks/useApi";
import type { Colaborador } from "@/types/api";

function formatCpf(cpf: string): string {
  if (cpf.length !== 11) return cpf;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}

export function ColaboradoresPage() {
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<Colaborador | null>(null);
  const [criadoMsg, setCriadoMsg] = useState<string | null>(null);
  // Inativos ficam escondidos por padrão: a lista do dia a dia é de quem está
  // trabalhando. O inativo não some do sistema — ele só deixa de abrir
  // atendimento novo e sai do cálculo de pendências.
  const [verInativos, setVerInativos] = useState(false);
  const { data: empresas } = useEmpresas();
  const atualizar = useAtualizarColaborador();
  const { data, isLoading } = useColaboradores({
    empresa_id: empresaId, q: q || undefined, incluir_inativos: verInativos,
  });

  async function alternarAtivo(c: Colaborador) {
    const acao = c.ativo ? "Inativar" : "Reativar";
    const aviso = c.ativo
      ? `Inativar ${c.nome}?

O cadastro continua no sistema e o histórico de ASOs é ` +
        "preservado, mas ele deixa de abrir atendimento novo e sai das pendências."
      : `Reativar ${c.nome}?`;
    if (!window.confirm(aviso)) return;
    try {
      await atualizar.mutateAsync({ id: c.id, patch: { ativo: !c.ativo } });
      setCriadoMsg(`${acao} ${c.nome}: feito.`);
      if (c.ativo) setVerInativos(true);   // pra ela ver onde o cadastro foi parar
    } catch { /* erro em atualizar.error */ }
  }

  const empresaNome = (id: number) =>
    empresas?.find((e) => e.id === id)?.razao_social ?? `#${id}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">Colaboradores</h2>
        <button className="btn-primary" onClick={() => setNovo(true)}>+ Novo colaborador</button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="input max-w-xs"
          value={empresaId ?? ""}
          onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Todas as empresas</option>
          {(empresas ?? []).map((e) => (
            <option key={e.id} value={e.id}>{e.razao_social}</option>
          ))}
        </select>
        <input
          className="input max-w-md"
          placeholder="Buscar por nome ou CPF…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-ink-muted">
          <input type="checkbox" checked={verInativos}
            onChange={(e) => setVerInativos(e.target.checked)} />
          Mostrar inativos
        </label>
      </div>
      {criadoMsg ? <p className="text-sm text-green-700">{criadoMsg}</p> : null}
      {atualizar.error ? (
        <p className="text-sm text-anarosa">{(atualizar.error as Error).message}</p>
      ) : null}

      {isLoading ? (
        <p className="text-ink-muted">Buscando…</p>
      ) : data && data.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">CPF</th>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Sexo</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    {c.nome}
                    {!c.ativo ? <span className="badge-inativo ml-2">inativo</span> : null}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{formatCpf(c.cpf)}</td>
                  <td className="px-4 py-2 text-ink-muted">{empresaNome(c.empresa_id)}</td>
                  <td className="px-4 py-2 text-ink-muted">{c.sexo ?? "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-sm font-medium text-anarosa hover:underline"
                      onClick={() => { setCriadoMsg(null); setEditando(c); }}
                    >
                      Corrigir
                    </button>
                    <button
                      className="ml-3 text-sm font-medium text-ink-muted hover:underline"
                      disabled={atualizar.isPending}
                      onClick={() => { setCriadoMsg(null); void alternarAtivo(c); }}
                    >
                      {c.ativo ? "Inativar" : "Reativar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-ink-muted">Nenhum colaborador encontrado.</p>
      )}

      {novo || editando ? (
        <ColaboradorFormModal
          colaborador={editando ?? undefined}
          onInativar={(c) => { setEditando(null); void alternarAtivo(c); }}
          onSaved={(c, editado) => {
            setNovo(false);
            setEditando(null);
            setCriadoMsg(
              editado ? `Cadastro de ${c.nome} corrigido.` : `Colaborador ${c.nome} cadastrado.`,
            );
            if (!editado) setQ(c.nome.slice(0, 20));
          }}
          onClose={() => { setNovo(false); setEditando(null); }}
        />
      ) : null}
    </div>
  );
}
